import React from "react";
import Button from "@source/components/UI/buttons/button/button";
import Radio from "@source/components/UI/buttons/radio/radio";
import SelectInput from "@source/components/UI/inputs/selectInput/selectInput";
import {ConsentStepsValues} from "@source/api/models/consentHelpers";
import {ConsentCategorySource, ConsentClinicalSigningMode, ConsentSignerRole} from "@source/api/models/consentModels";
import {PeoplePicker} from "./peoplePicker";
import {PersistStage, persistStageText, SignerPerson, signerLabel} from "./consentStepsShared";
import {StepFooter} from "./stepFooter";
import styles from "./consentSteps.module.scss";

export const SignerStep = ({
  categoryName,
  rolesTextValue,
  source,
  requiredRoles,
  values,
  employees,
  family,
  persistStage,
  formError,
  stale,
  onClinicalMode,
  onFamilyMode,
  onAddSigner,
  onRemoveSigner,
  onSelectClinicalSigner,
  onClearClinicalSigner,
  onFamilyCompleter,
  onCancel,
  onSave,
  onSend,
  onRefresh,
}: {
  categoryName?: string;
  rolesTextValue: string;
  source?: ConsentCategorySource;
  requiredRoles: ConsentSignerRole[];
  values: ConsentStepsValues;
  employees: SignerPerson[];
  family: SignerPerson[];
  persistStage: PersistStage;
  formError: string;
  stale: boolean;
  onClinicalMode: (mode: ConsentClinicalSigningMode) => void;
  onFamilyMode: (mode: ConsentClinicalSigningMode) => void;
  onAddSigner: (key: "employeeSignerIds" | "familyMemberIds", id: string) => void;
  onRemoveSigner: (key: "employeeSignerIds" | "familyMemberIds", id: string) => void;
  onSelectClinicalSigner: (id: string) => void;
  onClearClinicalSigner: () => void;
  onFamilyCompleter: (id: string) => void;
  onCancel: () => void;
  onSave: () => void;
  onSend: () => void;
  onRefresh: () => void;
}) => {
  const disabled = Boolean(persistStage);
  const needs = (role: ConsentSignerRole) => requiredRoles.includes(role);
  const familyOptions = family.filter((person) => values.familyMemberIds.includes(person._id));
  return <section className={styles.detailsStep}>
    <div className={styles.stepBody}>
      <div className={styles.summary}><strong>{categoryName}</strong><span>{rolesTextValue}</span></div>
      {needs(ConsentSignerRole.GP_CLINICAL) ? <section className={styles.section}>
        <h3>GP / Clinician signature</h3>
        <p>Choose whether the GP or clinician signs on this device or through their Brenna login.</p>
        <div className={styles.inputGroup}>
          <Radio name="clinical-signing-mode" value={ConsentClinicalSigningMode.BRENNA_USER} checked={values.clinicalSigningMode === ConsentClinicalSigningMode.BRENNA_USER} disabled={disabled} onChange={() => onClinicalMode(ConsentClinicalSigningMode.BRENNA_USER)} label="Select a Brenna employee"/>
          <Radio name="clinical-signing-mode" value={ConsentClinicalSigningMode.ON_SITE} checked={values.clinicalSigningMode === ConsentClinicalSigningMode.ON_SITE} disabled={disabled} onChange={() => onClinicalMode(ConsentClinicalSigningMode.ON_SITE)} label="GP / Clinician signs on site"/>
        </div>
        {values.clinicalSigningMode === ConsentClinicalSigningMode.ON_SITE ? <p>Their name is entered later, when a staff member verifies their identity on site.</p> : null}
      </section> : null}
      {needs(ConsentSignerRole.GP_CLINICAL) && values.clinicalSigningMode === ConsentClinicalSigningMode.BRENNA_USER ? <PeoplePicker id="clinical-signer" label="GP / Clinician" placeholder="Select an employee" people={employees} selected={values.clinicalSignerId ? [values.clinicalSignerId] : []} disabled={disabled} onAdd={onSelectClinicalSigner} onRemove={onClearClinicalSigner}/> : null}
      {needs(ConsentSignerRole.EMPLOYEE) ? <PeoplePicker id="employee-signers" label="Required Employee signatures" placeholder="Select an employee to add" people={employees} selected={values.employeeSignerIds} disabled={disabled} onAdd={(id) => onAddSigner("employeeSignerIds", id)} onRemove={(id) => onRemoveSigner("employeeSignerIds", id)}/> : null}
      {needs(ConsentSignerRole.FAMILY_MEMBER) ? <section className={styles.section}>
        <h3>Family member signature</h3>
        <p>Choose whether a family member signs on this device or through the family app.</p>
        <div className={styles.inputGroup}>
          <Radio name="family-signing-mode" value={ConsentClinicalSigningMode.BRENNA_USER} checked={values.familySigningMode === ConsentClinicalSigningMode.BRENNA_USER} disabled={disabled} onChange={() => onFamilyMode(ConsentClinicalSigningMode.BRENNA_USER)} label="Select Brenna family members"/>
          <Radio name="family-signing-mode" value={ConsentClinicalSigningMode.ON_SITE} checked={values.familySigningMode === ConsentClinicalSigningMode.ON_SITE} disabled={disabled} onChange={() => onFamilyMode(ConsentClinicalSigningMode.ON_SITE)} label="Family member signing on-site"/>
        </div>
        {values.familySigningMode === ConsentClinicalSigningMode.ON_SITE ? <p>Their name is entered later, when a staff member verifies their identity on site.</p> : null}
      </section> : null}
      {needs(ConsentSignerRole.FAMILY_MEMBER) && values.familySigningMode === ConsentClinicalSigningMode.BRENNA_USER ? <>
        <PeoplePicker id="family-signers" label="Required Family members signatures" placeholder="Select a family member to add" people={family} selected={values.familyMemberIds} disabled={disabled} onAdd={(id) => onAddSigner("familyMemberIds", id)} onRemove={(id) => onRemoveSigner("familyMemberIds", id)}/>
        {source === ConsentCategorySource.TEMPLATE && values.familyMemberIds.length > 1 ? <SelectInput id="family-field-completer" className={styles.formControl} label="Family member completing fields" placeholder="Select a family member" value={values.familyFieldCompleterId} items={familyOptions.map((person) => ({id: person._id, value: person._id, title: signerLabel(person)}))} disabled={disabled} handleChange={(event) => onFamilyCompleter(event.target.value)} required/> : null}
      </> : null}
      {stale ? <Button variant="outlined" onClick={onRefresh} disabled={disabled}>Refresh document</Button> : null}
    </div>
    <StepFooter
      error={formError}
      stageLabel={persistStage ? `${persistStageText[persistStage]}…` : undefined}
      disabled={disabled}
      onCancel={onCancel}
      onSave={onSave}
      onSend={onSend}
      saving={persistStage === "saving"}
      sending={persistStage === "sending"}
    />
  </section>;
};
